// Studio (was "Product ingestor") — hub → Studio. Takes an item from the
// supplier and makes it other's (CEO naming 2026-10-08).
//
//   Run a product — (CEO 2026-10-09) one product through every step, with a
//            button per step, an engine picker and "Compare engines"
//            (studio/RunProductTab.tsx). Engines — the default engine per step
//            and department, key status, cost (studio/EnginesTab.tsx).
//
// CEO 2026-10-05: build the ingestion app inside the control centre. Plan in
// docs/vendor-ingestion-app.md. Two tabs so far:
//
//   Health — per supplier and per website shelf: what is sellable, and what
//            holds the rest back (the readiness gaps the website obeys).
//   Photos — every colour's photos on a website shelf, checked and reviewable.
//            Hide a bad photo or pick the main one; the supplier's original is
//            never altered, and every decision can be undone.
//   Studio photos — each colour's photo in other's own studio frame (made by
//            scripts/studio-photos.ts, in batches). Look at a random sample
//            side by side with the supplier's, then approve or reject the
//            batch; reject single images. Nothing shows on the website until
//            a batch is approved.
//   Descriptions — our-voice drafts written from the supplier's facts, next to
//            the words the website shows today. Accept (edit first if you
//            like) or reject; only an accept changes the website.
//   Shelves — products whose names don't read like the shelf they're on.
//            Move one to the right shelf, or keep it where it is; either way
//            the decision survives the nightly re-filing.
import { useCallback, useEffect, useMemo, useState } from "react";
import { PackageSearch, Loader2, RefreshCw, EyeOff, Eye, Star, PenLine, Check, X, ArrowRight } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { RunProductTab } from "@/pages/studio/RunProductTab";
import { EnginesTab } from "@/pages/studio/EnginesTab";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

// ─── Types (mirror apps/api/src/services/ingestor.ts) ────────────────────────

interface HealthRow {
  key: string; label: string; listings: number; ready: number;
  inactive: number; offer: number; category: number; price: number; image: number; description: number; colour: number;
}
interface Health {
  measuredAt: string | null;
  totals: HealthRow;
  bySupplier: HealthRow[];
  byShelf: HealthRow[];
  photos: { checked: number; broken: number; small: number; lastCheckedAt: string | null };
}
interface ReviewPhoto {
  src: string; label: string;
  check: { httpStatus: number; width: number | null; height: number | null; checkedAt: string } | null;
  flag: "BROKEN" | "SMALL" | "UNCHECKED" | null;
  decision: "hidden" | "hero" | null;
}
interface ReviewColour { name: string; own: boolean; photos: ReviewPhoto[]; flags: Array<"NO_OWN" | "BROKEN" | "SMALL" | "UNCHECKED"> }
interface ReviewListing { id: string; sku: string | null; name: string; vendor: string | null; shelf: string; ready: boolean; colours: ReviewColour[]; needsAttention: boolean }
interface JobStatus { running: boolean; total: number; done: number; broken: number; small: number; startedAt: string | null; finishedAt: string | null; error: string | null }

const SHELVES: Array<[string, string]> = [
  ["polos", "Polos"], ["t_shirts", "T-shirts"], ["sweatshirts_fleece", "Sweatshirts & fleece"],
  ["outerwear", "Outerwear"], ["woven_shirts", "Woven shirts"], ["activewear", "Activewear"],
];

const GAPS: Array<[keyof HealthRow, string, string]> = [
  ["price", "No price", "No usable price from the supplier"],
  ["image", "No photo", "No photo at all"],
  ["description", "No description", "Description missing or too short"],
  ["category", "No shelf", "Not filed on a shelf"],
  ["colour", "No colours", "Colours not recognised"],
  ["offer", "No live supplier listing", "No live supplier offer"],
];

const n = (x: number) => x.toLocaleString("en-US");
const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
const ago = (iso: string | null) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "never");

// ─── Page ────────────────────────────────────────────────────────────────────

type Tab = "run" | "health" | "photos" | "studio" | "descriptions" | "shelves" | "engines";
const TABS: readonly Tab[] = ["run", "health", "photos", "studio", "descriptions", "shelves", "engines"];

export function IngestorPage() {
  // Deep link from hub → Products: /studio?tab=photos&shelf=polos
  // Workbench: /studio?tab=run&product=<id> (hub → Products → "Open in Studio").
  const [params, setParams] = useSearchParams();
  const linkedTab = params.get("tab");
  const linkedShelf = params.get("shelf");
  const product = params.get("product");
  const startShelf = SHELVES.some(([k]) => k === linkedShelf) ? linkedShelf! : undefined;
  const tab: Tab = TABS.includes(linkedTab as Tab) ? (linkedTab as Tab) : "run";
  const setTab = useCallback((t: Tab) => setParams((p) => { const n = new URLSearchParams(p); n.set("tab", t); if (t !== "photos") n.delete("shelf"); return n; }, { replace: true }), [setParams]);
  const openProduct = useCallback((id: string | null) => setParams((p) => { const n = new URLSearchParams(p); n.set("tab", "run"); if (id) n.set("product", id); else n.delete("product"); return n; }), [setParams]);
  return (
    <div className="mx-auto w-full max-w-[1180px] px-6 py-bay md:px-10">
      <header className="flex flex-col gap-3 border-b border-rule pb-gutter">
        <div className="flex items-center gap-3">
          <PackageSearch className="h-6 w-6" strokeWidth={1.5} aria-hidden />
          <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px]">Studio</h1>
        </div>
        <p className="max-w-[62ch] text-body text-ink-muted">
          What our catalogue looks like to a client, and what stops a product from selling.
          Your decisions here change only what the website shows; the supplier's own data is never
          altered, and every decision can be undone.{" "}
          <Link to="/products" className="underline underline-offset-2">Find, edit or move any product in Products.</Link>
        </p>
        <div className="-mx-1 mt-2 flex gap-6 overflow-x-auto px-1" role="tablist" aria-label="Studio sections">
          {([["run", "Run a product"], ["health", "Catalogue health"], ["photos", "Photos"], ["studio", "Studio photos"], ["descriptions", "Descriptions"], ["shelves", "Shelves"], ["engines", "Engines"]] as const).map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              onClick={() => setTab(k)}
              aria-selected={tab === k}
              className={cn("shrink-0 whitespace-nowrap border-b-2 pb-2 text-label font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus", tab === k ? "border-ink text-ink" : "border-transparent text-ink-muted hover:text-ink")}
            >
              {label}
            </button>
          ))}
        </div>
      </header>
      {tab === "run" ? <RunProductTab productId={product} onOpen={openProduct} />
        : tab === "engines" ? <EnginesTab />
        : tab === "health" ? <HealthTab onReviewPhotos={() => setTab("photos")} />
        : tab === "photos" ? <PhotosTab initialShelf={startShelf} />
        : tab === "studio" ? <StudioPhotosTab />
        : tab === "descriptions" ? <DescriptionsTab />
        : <ShelvesTab />}
    </div>
  );
}

// ─── Health ──────────────────────────────────────────────────────────────────

function HealthTab({ onReviewPhotos }: { onReviewPhotos: () => void }) {
  const [data, setData] = useState<Health | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<Health>("/ingestor/health").then(setData).catch((e) => setError(apiErrorMessage(e, "Could not load catalogue health.")));
  }, []);

  if (error) return <p className="mt-bay text-label text-risk">{error}</p>;
  if (!data) return <Loading label="Measuring the catalogue…" />;
  const t = data.totals;
  return (
    <div className="mt-bay flex flex-col gap-bay">
      <section className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-3">
        <Stat label="Sellable on the website" value={n(t.ready)} note={`${pct(t.ready, t.listings)}% of ${n(t.listings)} listings`} />
        <Stat label="Held back" value={n(t.listings - t.ready)} note={`Biggest reason: ${biggestGap(t)}`} />
        <Stat
          label="Photos checked"
          value={n(data.photos.checked)}
          note={data.photos.checked ? `${n(data.photos.broken)} broken · ${n(data.photos.small)} too small` : "None yet — run a check on the Photos tab"}
          action={<Button size="sm" variant="outline" className="mt-2 self-start" onClick={onReviewPhotos}>Review photos</Button>}
        />
      </section>

      <p className="-mb-4 max-w-[70ch] text-meta text-ink-muted">
        Each column counts products held back for that reason. A product can appear in more than one column.
        Sellable is marked Good (80% or more), Fair (50–79%) or Low (under 50%).
      </p>
      <HealthTable title="Website shelves" rows={data.byShelf} />
      <HealthTable title="By supplier" rows={data.bySupplier.filter((r) => r.listings >= 5)} />
      <p className="text-meta text-ink-muted">
        Readiness last measured {ago(data.measuredAt)}. Suppliers with fewer than five listings are not shown.
      </p>
    </div>
  );
}

function biggestGap(r: HealthRow): string {
  const [key, label] = [...GAPS].sort((a, b) => (r[b[0]] as number) - (r[a[0]] as number))[0]!;
  return `${label.toLowerCase()} (${n(r[key] as number)})`;
}

function Stat({ label, value, note, action }: { label: string; value: string; note: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 bg-white p-6">
      <span className="text-meta font-semibold uppercase tracking-wide text-ink-muted">{label}</span>
      <span className="text-title font-bold tabular-nums">{value}</span>
      <span className="text-meta text-ink-muted">{note}</span>
      {action}
    </div>
  );
}

function HealthTable({ title, rows }: { title: string; rows: HealthRow[] }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-label font-bold">{title}</h2>
      <div className="overflow-x-auto rounded-lg border border-rule">
        <table className="w-full min-w-[760px] text-left text-meta tabular-nums">
          <caption className="sr-only">{title}: listings, sellable share and reasons products are held back</caption>
          <thead className="bg-paper text-ink-muted">
            <tr>
              <th scope="col" className="sticky left-0 bg-paper px-4 py-3 font-semibold">Name</th>
              <th scope="col" className="px-4 py-3 text-right font-semibold">Listings</th>
              <th scope="col" className="px-4 py-3 text-right font-semibold">Sellable</th>
              {GAPS.map(([k, label, hint]) => (
                <th key={k} scope="col" className="px-4 py-3 text-right font-semibold" title={hint}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-rule">
                <th scope="row" className="sticky left-0 bg-white px-4 py-3 text-left font-semibold">{r.label}</th>
                <td className="px-4 py-3 text-right">{n(r.listings)}</td>
                <td className="px-4 py-3 text-right">
                  <span className={cn("inline-block whitespace-nowrap rounded px-1.5", pct(r.ready, r.listings) >= 80 ? "bg-ok-bg text-ok" : pct(r.ready, r.listings) >= 50 ? "bg-warn-bg text-warn" : "bg-risk-bg text-risk")}>
                    {pct(r.ready, r.listings) >= 80 ? "Good" : pct(r.ready, r.listings) >= 50 ? "Fair" : "Low"} · {pct(r.ready, r.listings)}%
                  </span>
                </td>
                {GAPS.map(([k]) => (
                  <td key={k} className={cn("px-4 py-3 text-right", (r[k] as number) === 0 && "text-ink-muted/50")}>{n(r[k] as number)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ─── Photos ──────────────────────────────────────────────────────────────────

function PhotosTab({ initialShelf }: { initialShelf?: string }) {
  const [shelf, setShelf] = useState(initialShelf ?? "polos");
  const [attention, setAttention] = useState(true);
  const [listings, setListings] = useState<ReviewListing[]>([]);
  const [next, setNext] = useState<number | null>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (fresh: boolean, offset: number) => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ shelf, attention: attention ? "1" : "0", offset: String(offset) });
      const r = await api<{ listings: ReviewListing[]; nextOffset: number | null }>(`/ingestor/photos?${qs}`);
      setListings((prev) => (fresh ? r.listings : [...prev, ...r.listings]));
      setNext(r.listings.length ? r.nextOffset : null);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load photos."));
    } finally {
      setLoading(false);
    }
  }, [shelf, attention]);

  useEffect(() => { void load(true, 0); }, [load]);

  /** Throws on failure so the card can show the error beside the photo. */
  const decide = async (l: ReviewListing, c: ReviewColour, p: ReviewPhoto, action: "hide" | "hero" | "clear") => {
    {
      await api("/ingestor/photo-decision", { method: "POST", body: JSON.stringify({ listingId: l.id, colour: c.name, src: p.src, action }) });
      setListings((prev) => prev.map((x) => x.id !== l.id ? x : {
        ...x,
        colours: x.colours.map((y) => y.name !== c.name ? y : {
          ...y,
          photos: y.photos.map((z) => {
            if (z.src === p.src) return { ...z, decision: action === "clear" ? null : action === "hide" ? "hidden" : "hero" };
            if (action === "hero" && z.decision === "hero") return { ...z, decision: null };
            return z;
          }),
        }),
      }));
    }
  };

  return (
    <div className="mt-bay flex flex-col gap-gutter">
      <PhotoCheckBar shelf={shelf} onFinished={() => void load(true, 0)} />

      <div className="flex flex-wrap items-center gap-2">
        <ShelfPills shelf={shelf} onChange={setShelf} />
        <label className="ml-auto flex items-center gap-2 text-meta">
          <input type="checkbox" checked={attention} onChange={(e) => setAttention(e.target.checked)} />
          Only products that need attention
        </label>
      </div>

      <Legend />
      {error && <p className="text-label text-risk">{error}</p>}
      {!loading && listings.length === 0 && !error && (
        <p className="rounded-lg border border-rule bg-paper p-6 text-label text-ink-muted">
          {attention
            ? "Nothing flagged yet. If this shelf's photos haven't been checked, run the check above to find broken or blurry ones."
            : "No products on this shelf."}
        </p>
      )}

      <div className="flex flex-col gap-gutter">
        {listings.map((l) => <ListingCard key={l.id} listing={l} onDecide={decide} />)}
      </div>

      {loading ? <Loading inline label="Loading photos…" /> : next !== null && listings.length > 0 && (
        <Button variant="outline" className="self-center" onClick={() => void load(false, next)}>Show more</Button>
      )}
    </div>
  );
}

function PhotoCheckBar({ shelf, onFinished }: { shelf: string; onFinished: () => void }) {
  const [job, setJob] = useState<JobStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lost, setLost] = useState(0);
  const label = useMemo(() => SHELVES.find(([k]) => k === shelf)?.[1] ?? shelf, [shelf]);

  useEffect(() => {
    let alive = true;
    let wasRunning = false;
    const tick = async () => {
      try {
        const s = await api<JobStatus>("/ingestor/photo-check");
        if (!alive) return;
        setJob(s);
        setLost(0);
        if (wasRunning && !s.running) onFinished();
        wasRunning = s.running;
      } catch {
        if (alive) setLost((x) => x + 1);
      }
    };
    void tick();
    const t = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(t); };
  }, [onFinished]);

  const start = async () => {
    setError(null);
    try {
      setJob(await api<JobStatus>("/ingestor/photo-check", { method: "POST", body: JSON.stringify({ shelf }) }));
    } catch (e) {
      setError(apiErrorMessage(e, "Could not start the photo check."));
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-4 rounded-lg border border-rule bg-paper px-5 py-4">
      {job?.running ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          <span className="text-label">Checking photos: {n(job.done)} of {n(job.total)} · {n(job.broken)} broken · {n(job.small)} too small</span>
        </>
      ) : (
        <>
          <span className="text-label">
            {job?.finishedAt
              ? `Last check finished ${ago(job.finishedAt)}: ${n(job.total)} photos, ${n(job.broken)} broken, ${n(job.small)} too small.`
              : "Photos are checked on request: does each one load, and is it sharp enough for a product page."}
          </span>
          <Button size="sm" className="ml-auto" onClick={start}>
            <RefreshCw className="h-3.5 w-3.5" aria-hidden /> Check {label} photos
          </Button>
        </>
      )}
      {(error || job?.error) && <span className="w-full text-meta text-risk">{error ?? job?.error}</span>}
      {lost >= 3 && <span className="w-full text-meta text-warn">Lost contact with the server; retrying every few seconds.</span>}
    </div>
  );
}

const FLAG_TEXT: Record<string, [string, string]> = {
  NO_OWN: ["No photo of this colour", "bg-warn-bg text-warn"],
  BROKEN: ["Photo doesn't load", "bg-risk-bg text-risk"],
  SMALL: ["Too small", "bg-warn-bg text-warn"],
  UNCHECKED: ["Not checked yet", "bg-paper text-ink-muted"],
};

function Legend() {
  return (
    <p className="text-meta text-ink-muted">
      Use the eye to hide a photo and the star to make it the main photo for that colour. Hidden photos stay here,
      faded, with a link to show them again.
    </p>
  );
}

function ListingCard({ listing: l, onDecide }: {
  listing: ReviewListing;
  onDecide: (l: ReviewListing, c: ReviewColour, p: ReviewPhoto, action: "hide" | "hero" | "clear") => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const act = async (c: ReviewColour, p: ReviewPhoto, a: "hide" | "hero" | "clear") => {
    if (busy) return;
    setBusy(p.src);
    setError(null);
    try {
      await onDecide(l, c, p, a);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not save that decision. Please try again."));
    } finally {
      setBusy(null);
    }
  };
  const flagged = l.colours.filter((c) => c.flags.some((f) => f !== "UNCHECKED"));
  const shown = open ? l.colours : (flagged.length ? flagged : l.colours).slice(0, 6);
  return (
    <article className="rounded-lg border border-rule bg-white p-5">
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-label font-bold">{l.name}</h3>
        <span className="text-meta text-ink-muted">{[l.sku, l.vendor].filter(Boolean).join(" · ")}</span>
        <span className={cn("ml-auto rounded px-2 py-0.5 text-meta font-semibold", l.ready ? "bg-ok-bg text-ok" : "bg-risk-bg text-risk")}>
          {l.ready ? "On the website" : "Held back"}
        </span>
      </header>
      <p className="mt-1 text-meta text-ink-muted">
        {l.colours.length} colours{flagged.length ? ` · ${flagged.length} need attention` : " · all look fine"}
      </p>
      {error && <p className="mt-2 text-meta text-risk" role="alert">{error}</p>}
      {l.colours.length === 0 && <p className="mt-3 text-meta text-risk">No colours come through from the supplier, so the website can't show this product.</p>}
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((c) => (
          <div key={c.name} className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-meta font-semibold">{c.name}</span>
              {c.flags.map((f) => (
                <span key={f} className={cn("rounded px-1.5 py-0.5 text-[12px] font-semibold", FLAG_TEXT[f]![1])}>{FLAG_TEXT[f]![0]}</span>
              ))}
            </div>
            <div className="flex gap-2">
              {c.photos.map((p) => <PhotoTile key={p.src} photo={p} busy={busy === p.src} onAction={(a) => void act(c, p, a)} />)}
            </div>
          </div>
        ))}
      </div>
      {l.colours.length > shown.length && (
        <button type="button" className="mt-3 text-meta font-semibold underline" onClick={() => setOpen(true)}>
          Show all {l.colours.length} colours
        </button>
      )}
    </article>
  );
}

function PhotoTile({ photo: p, busy, onAction }: { photo: ReviewPhoto; busy: boolean; onAction: (a: "hide" | "hero" | "clear") => void }) {
  const hidden = p.decision === "hidden";
  const size = p.check?.width && p.check?.height ? `${p.check.width}×${p.check.height}` : p.flag === "UNCHECKED" ? "not checked" : "";
  return (
    <figure className="group relative w-[104px]">
      <div className={cn("relative h-[104px] w-[104px] overflow-hidden rounded-md border bg-paper", p.flag === "BROKEN" ? "border-risk" : p.decision === "hero" ? "border-ink border-2" : "border-rule", hidden && "opacity-30")}>
        <img src={p.src} alt={`${p.label}`} loading="lazy" className="h-full w-full object-contain" />
        {p.decision === "hero" && <Star className="absolute left-1 top-1 h-4 w-4 fill-ink text-ink" aria-label="Main photo" />}
        {!hidden && (
          <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 bg-white/90 py-0.5 opacity-70 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
            <IconButton label="Hide this photo" disabled={busy} onClick={() => onAction("hide")}><EyeOff className="h-4 w-4" /></IconButton>
            <IconButton label={p.decision === "hero" ? "Stop using as main photo" : "Make this the main photo"} disabled={busy} onClick={() => onAction(p.decision === "hero" ? "clear" : "hero")}>
              <Star className={cn("h-4 w-4", p.decision === "hero" && "fill-ink")} />
            </IconButton>
          </div>
        )}
        {busy && <Loader2 className="absolute right-1 top-1 h-4 w-4 animate-spin" aria-label="Saving" />}
      </div>
      <figcaption className="mt-1 text-[12px] leading-tight text-ink-muted">
        {p.label}{size ? ` · ${size}` : ""}
      </figcaption>
      {hidden && (
        <button type="button" disabled={busy} onClick={() => onAction("clear")} className="mt-1 flex items-center gap-1 text-[12px] font-semibold underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus">
          <Eye className="h-3.5 w-3.5" aria-hidden /> Hidden · show again
        </button>
      )}
    </figure>
  );
}

function IconButton({ label, onClick, disabled, children }: { label: string; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button type="button" title={label} aria-label={label} onClick={onClick} disabled={disabled} className="rounded p-1.5 hover:bg-paper disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus">
      {children}
    </button>
  );
}

function Loading({ label, inline }: { label: string; inline?: boolean }) {
  return (
    <p className={cn("flex items-center gap-2 text-label text-ink-muted", !inline && "mt-bay")}>
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> {label}
    </p>
  );
}

// ─── Descriptions ────────────────────────────────────────────────────────────

interface Draft {
  id: string; listingId: string; sku: string | null; name: string; brand: string | null; vendor: string;
  ready: boolean; current: string | null; currentSource: string; supplier: string | null; draft: string; createdAt: string;
  image: string | null;
}
interface DraftJob { running: boolean; shelf: string | null; total: number; written: number; failed: number; finishedAt: string | null; error: string | null }

interface DescriptionBatch { label: string; pending: number; accepted: number; rejected: number; createdAt: string }

/** Supplier batches (scripts/description-drafts.ts): read a sample, reject or
 *  edit any bad ones, then accept the rest of the batch in one go. */
function DescriptionBatches({ selected, onSelect, refreshKey }: { selected: string | null; onSelect: (label: string | null) => void; refreshKey: number }) {
  const [batches, setBatches] = useState<DescriptionBatch[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const load = useCallback(() => {
    api<{ batches: DescriptionBatch[] }>("/ingestor/description-batches").then((r) => setBatches(r.batches)).catch(() => setBatches([]));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (batches.length === 0) return null;
  const acceptRest = async (b: DescriptionBatch) => {
    if (!window.confirm(`Accept the ${n(b.pending)} drafts still waiting in "${b.label}"? They replace the text on the website.`)) return;
    setBusy(b.label);
    setNote(null);
    try {
      const r = await api<{ changed: number }>("/ingestor/description-batches/accept", { method: "POST", body: JSON.stringify({ label: b.label }) });
      setNote(`${n(r.changed)} descriptions updated from "${b.label}".`);
      onSelect(null);
      load();
    } catch (e) {
      setNote(apiErrorMessage(e, "Could not accept that batch."));
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="flex flex-col gap-2 rounded-lg border border-rule bg-paper px-5 py-4">
      <h3 className="text-label font-bold">Supplier batches</h3>
      <p className="text-meta text-ink-muted">Read a sample of a batch, reject or fix any bad drafts, then accept the rest of the batch.</p>
      {batches.map((b) => (
        <div key={b.label} className="flex flex-wrap items-center gap-3 border-t border-rule pt-2">
          <span className="text-label">{b.label}</span>
          <span className="text-meta text-ink-muted">{n(b.pending)} waiting · {n(b.accepted)} accepted · {n(b.rejected)} rejected</span>
          {b.pending > 0 && (
            <span className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={() => onSelect(selected === b.label ? null : b.label)}>
                {selected === b.label ? "Back to shelves" : "Review a sample"}
              </Button>
              <Button size="sm" onClick={() => acceptRest(b)} disabled={busy !== null}>
                <Check className="h-3.5 w-3.5" aria-hidden /> Accept the rest ({n(b.pending)})
              </Button>
            </span>
          )}
        </div>
      ))}
      {note && <span className="text-meta">{note}</span>}
    </section>
  );
}

function DescriptionsTab() {
  const [shelf, setShelf] = useState("polos");
  const [batch, setBatch] = useState<string | null>(null);
  const [batchTick, setBatchTick] = useState(0);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [pending, setPending] = useState(0);
  const [next, setNext] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (fresh: boolean, offset: number) => {
    setLoading(true);
    setError(null);
    try {
      const r = await api<{ drafts: Draft[]; pending: number; nextOffset: number | null }>(`/ingestor/descriptions?${new URLSearchParams({ ...(batch ? { batch } : { shelf }), offset: String(offset) })}`);
      setDrafts((prev) => (fresh ? r.drafts : [...prev, ...r.drafts]));
      setPending(r.pending);
      setNext(r.nextOffset);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load drafts."));
    } finally {
      setLoading(false);
    }
  }, [shelf, batch]);
  useEffect(() => { void load(true, 0); }, [load]);

  const decided = (id: string) => {
    setDrafts((prev) => prev.filter((d) => d.id !== id));
    setPending((n) => Math.max(0, n - 1));
    if (batch) setBatchTick((t) => t + 1);
  };

  return (
    <div className="mt-bay flex flex-col gap-gutter">
      <DraftJobBar shelf={shelf} onFinished={() => void load(true, 0)} />
      <DescriptionBatches selected={batch} onSelect={setBatch} refreshKey={batchTick} />
      <div className="flex flex-wrap items-center gap-2">
        {batch
          ? <span className="text-label font-semibold">Sample of “{batch}”</span>
          : <ShelfPills shelf={shelf} onChange={setShelf} />}
        <span className="ml-auto text-meta text-ink-muted">{n(pending)} waiting for review</span>
      </div>
      <p className="max-w-[70ch] text-meta text-ink-muted">
        Drafts are written only from what the supplier tells us; they never add a material, size or claim.
        Nothing changes on the website until you accept. You can edit the words before accepting.
      </p>
      {error && <p className="text-label text-risk">{error}</p>}
      {!loading && drafts.length === 0 && !error && (
        <p className="rounded-lg border border-rule bg-paper p-6 text-label text-ink-muted">
          No drafts waiting on this shelf. Write some with the button above.
        </p>
      )}
      <div className="flex flex-col gap-gutter">
        {drafts.map((d) => <DraftCard key={d.id} draft={d} onDecided={() => decided(d.id)} />)}
      </div>
      {loading ? <Loading inline label="Loading drafts…" /> : next !== null && (
        <Button variant="outline" className="self-center" onClick={() => void load(false, next)}>Show more</Button>
      )}
    </div>
  );
}

function DraftJobBar({ shelf, onFinished }: { shelf: string; onFinished: () => void }) {
  const [job, setJob] = useState<DraftJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lost, setLost] = useState(0);
  const label = useMemo(() => SHELVES.find(([k]) => k === shelf)?.[1] ?? shelf, [shelf]);
  useEffect(() => {
    let alive = true;
    let wasRunning = false;
    const tick = async () => {
      try {
        const s = await api<DraftJob>("/ingestor/description-drafts");
        if (!alive) return;
        setJob(s);
        setLost(0);
        if (wasRunning && !s.running) onFinished();
        wasRunning = s.running;
      } catch {
        if (alive) setLost((x) => x + 1);
      }
    };
    void tick();
    const t = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(t); };
  }, [onFinished]);

  const start = async () => {
    setError(null);
    try {
      setJob(await api<DraftJob>("/ingestor/description-drafts", { method: "POST", body: JSON.stringify({ shelf, count: 20 }) }));
    } catch (e) {
      setError(apiErrorMessage(e, "Could not start drafting."));
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-4 rounded-lg border border-rule bg-paper px-5 py-4">
      {job?.running ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          <span className="text-label">Writing drafts: {n(job.written + job.failed)} of {n(job.total)}{job.failed ? ` · ${n(job.failed)} couldn't be written` : ""}</span>
        </>
      ) : (
        <>
          <span className="text-label">
            {job?.finishedAt && !job.error
              ? `Last run wrote ${n(job.written)} draft${job.written === 1 ? "" : "s"}${job.failed ? ` (${n(job.failed)} skipped)` : ""}.`
              : "Drafts are written 20 at a time, for products that still show the supplier's own wording. A run takes a minute or two."}
          </span>
          <Button size="sm" className="ml-auto" onClick={start}>
            <PenLine className="h-3.5 w-3.5" aria-hidden /> Write 20 {label.toLowerCase()} drafts
          </Button>
        </>
      )}
      {(error || job?.error) && <span className="w-full text-meta text-risk">{error ?? job?.error}</span>}
      {lost >= 3 && <span className="w-full text-meta text-warn">Lost contact with the server; retrying every few seconds.</span>}
    </div>
  );
}

function DraftCard({ draft: d, onDecided }: { draft: Draft; onDecided: () => void }) {
  const [text, setText] = useState(d.draft);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const edited = text.replace(/\s+/g, " ").trim() !== d.draft;
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;

  const decide = async (action: "accept" | "reject") => {
    setBusy(true);
    setError(null);
    try {
      await api("/ingestor/description-decision", {
        method: "POST",
        body: JSON.stringify({ draftId: d.id, action, ...(action === "accept" && edited ? { text } : {}) }),
      });
      onDecided();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not save that decision."));
      setBusy(false);
    }
  };

  return (
    <article className="grid gap-5 rounded-lg border border-rule bg-white p-5 md:grid-cols-[96px_1fr]">
      <div className="h-24 w-24 overflow-hidden rounded-md border border-rule bg-paper">
        {d.image && <img src={d.image} alt="" loading="lazy" className="h-full w-full object-contain" />}
      </div>
      <div className="flex flex-col gap-4">
        <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 className="text-label font-bold">{d.name}</h3>
          <span className="text-meta text-ink-muted">{[d.brand, d.sku, d.vendor].filter(Boolean).join(" · ")}</span>
          <span className={cn("ml-auto rounded px-2 py-0.5 text-meta font-semibold", d.ready ? "bg-ok-bg text-ok" : "bg-risk-bg text-risk")}>
            {d.ready ? "On the website" : "Held back"}
          </span>
        </header>
        <div className="grid gap-5 lg:grid-cols-2">
          <section className="flex flex-col gap-2">
            <h4 className="text-meta font-semibold uppercase tracking-wide text-ink-muted">On the website today</h4>
            <p className="whitespace-pre-line text-label text-ink-muted">{d.current?.trim() || "No description."}</p>
          </section>
          <section className="flex flex-col gap-2">
            <h4 className="flex items-center gap-2 text-meta font-semibold uppercase tracking-wide text-ink-muted">
              Our draft {edited && <span className="rounded bg-warn-bg px-1.5 text-[12px] normal-case tracking-normal text-warn">edited</span>}
            </h4>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={5}
              aria-label={`Draft description for ${d.name}`}
              className="w-full resize-y rounded-md border border-rule p-3 text-label leading-relaxed focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus"
            />
            <span className="text-[12px] text-ink-muted">{words} words{words < 8 ? " · needs at least 8 words to accept" : ""}</span>
          </section>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm" onClick={() => decide("accept")} disabled={busy || words < 8}>
            <Check className="h-3.5 w-3.5" aria-hidden /> {edited ? "Accept my edit" : "Accept"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => decide("reject")} disabled={busy}>
            <X className="h-3.5 w-3.5" aria-hidden /> Reject
          </Button>
          {edited && (
            <button type="button" className="text-meta underline" onClick={() => setText(d.draft)}>Undo my edits</button>
          )}
          <span className="text-meta text-ink-muted">Accepting replaces the text on the website.</span>
          {error && <span className="text-meta text-risk">{error}</span>}
        </div>
      </div>
    </article>
  );
}

// ─── Shelves ─────────────────────────────────────────────────────────────────

interface Suspect { id: string; sku: string | null; name: string; vendor: string | null; ready: boolean; reason: string; image: string | null }
interface ShelfOption { slug: string; name: string; parent: string | null }

function ShelvesTab() {
  const [shelf, setShelf] = useState("activewear");
  const [data, setData] = useState<{ scanned: number; suspects: Suspect[] } | null>(null);
  const [options, setOptions] = useState<ShelfOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ shelves: ShelfOption[] }>("/ingestor/shelves/options")
      .then((r) => setOptions(r.shelves))
      .catch((e) => setError(apiErrorMessage(e, "Could not load the list of shelves, so products can't be moved right now.")));
  }, []);
  useEffect(() => {
    setData(null);
    setError(null);
    api<{ scanned: number; suspects: Suspect[] }>(`/ingestor/shelves/suspects?shelf=${shelf}`)
      .then(setData)
      .catch((e) => setError(apiErrorMessage(e, "Could not check this shelf.")));
  }, [shelf]);

  const done = (id: string) => setData((d) => (d ? { ...d, suspects: d.suspects.filter((x) => x.id !== id) } : d));
  const label = SHELVES.find(([k]) => k === shelf)?.[1] ?? shelf;

  return (
    <div className="mt-bay flex flex-col gap-gutter">
      <div className="flex flex-wrap items-center gap-2">
        <ShelfPills shelf={shelf} onChange={setShelf} />
        {data && <span className="ml-auto text-meta text-ink-muted">{n(data.suspects.length)} to check of {n(data.scanned)} on this shelf</span>}
      </div>
      <p className="max-w-[70ch] text-meta text-ink-muted">
        These products are on the {label.toLowerCase()} shelf but their names don't read like it. Move each one to the
        right shelf, or keep it here if it belongs. Your choice is kept when the catalogue is re-filed each night.
      </p>
      {error && <p className="text-label text-risk">{error}</p>}
      {!data && !error && <Loading inline label="Checking the shelf…" />}
      {data && data.suspects.length === 0 && (
        <p className="rounded-lg border border-rule bg-paper p-6 text-label text-ink-muted">Everything on this shelf reads like it belongs.</p>
      )}
      <div className="flex flex-col divide-y divide-rule rounded-lg border border-rule bg-white">
        {data?.suspects.map((x) => <SuspectRow key={x.id} suspect={x} shelf={shelf} options={options} onDone={() => done(x.id)} />)}
      </div>
    </div>
  );
}

function SuspectRow({ suspect: x, shelf, options, onDone }: { suspect: Suspect; shelf: string; options: ShelfOption[]; onDone: () => void }) {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const move = async (slug: string) => {
    setBusy(true);
    setError(null);
    try {
      await api("/ingestor/shelves/move", { method: "POST", body: JSON.stringify({ listingId: x.id, toSlug: slug }) });
      onDone();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not move that product."));
      setBusy(false);
    }
  };
  const groups = useMemo(() => {
    const m = new Map<string, ShelfOption[]>();
    for (const o of options) { const k = o.parent ?? o.name; m.set(k, [...(m.get(k) ?? []), o]); }
    return [...m.entries()];
  }, [options]);

  return (
    <div className="flex flex-wrap items-center gap-4 p-4">
      <div className="h-14 w-14 shrink-0 overflow-hidden rounded border border-rule bg-paper">
        {x.image && <img src={x.image} alt="" loading="lazy" className="h-full w-full object-contain" />}
      </div>
      <div className="min-w-[220px] flex-1">
        <p className="text-label font-semibold">{x.name}</p>
        <p className="flex flex-wrap items-center gap-2 text-meta text-ink-muted">
          <span>{[x.sku, x.vendor].filter(Boolean).join(" · ")}{x.ready ? " · on the website" : ""}</span>
          <span className="rounded bg-warn-bg px-1.5 py-0.5 text-[12px] font-semibold text-warn">{x.reason}</span>
        </p>
        {error && <p className="text-meta text-risk">{error}</p>}
      </div>
      <label className="sr-only" htmlFor={`to-${x.id}`}>Move {x.name} to</label>
      <select
        id={`to-${x.id}`}
        value={to}
        onChange={(e) => setTo(e.target.value)}
        className="h-8 max-w-[240px] rounded-md border border-rule bg-white px-2 text-meta"
      >
        <option value="">Move to…</option>
        {groups.map(([g, items]) => (
          <optgroup key={g} label={g}>
            {items.map((o) => <option key={o.slug} value={o.slug} disabled={o.slug === shelf}>{o.name}</option>)}
          </optgroup>
        ))}
      </select>
      <Button size="sm" disabled={!to || busy || options.length === 0} onClick={() => move(to)}>
        <ArrowRight className="h-3.5 w-3.5" aria-hidden /> Move
      </Button>
      <Button size="sm" variant="outline" disabled={busy} onClick={() => move(shelf)} title="It belongs on this shelf; stop listing it here">Keep here</Button>
    </div>
  );
}

/** The website's apparel shelves as a row of toggles — one copy for every tab. */
function ShelfPills({ shelf, onChange }: { shelf: string; onChange: (k: string) => void }) {
  return (
    <>
      {SHELVES.map(([k, label]) => (
        <button
          key={k}
          type="button"
          aria-pressed={shelf === k}
          onClick={() => onChange(k)}
          className={cn(
            "rounded-full border px-3.5 py-1.5 text-meta focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus",
            shelf === k ? "border-ink bg-ink text-white" : "border-rule bg-white hover:bg-paper",
          )}
        >
          {label}
        </button>
      ))}
    </>
  );
}

// ─── Studio photos ───────────────────────────────────────────────────────────
//
// Batches come from scripts/studio-photos.ts. A random sample of 20 side by
// side with the supplier's photo; approve or reject the whole batch, reject
// single images. Only approved images ever show on the website.

interface StudioBatch {
  id: string; label: string; status: string; createdAt: string; approvedAt: string | null;
  generated: number; approved: number; rejected: number; failed: number;
}
interface StudioImage { id: string; listingId: string; listingName: string; colour: string; sourceUrl: string; studioUrl: string | null; status: string; reason: string | null }

const BATCH_STATUS: Record<string, [string, string]> = {
  running: ["Being made", "bg-paper text-ink-muted"],
  review: ["Waiting for review", "bg-warn-bg text-warn"],
  approved: ["Approved — live", "bg-ok-bg text-ok"],
  rejected: ["Rejected", "bg-risk-bg text-risk"],
};

function StudioPhotosTab() {
  const [batches, setBatches] = useState<StudioBatch[] | null>(null);
  const [notSetUp, setNotSetUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const load = useCallback(() => {
    api<{ batches: StudioBatch[]; notSetUp?: boolean }>("/ingestor/studio/batches")
      .then((r) => { setBatches(r.batches); setNotSetUp(!!r.notSetUp); setOpen((o) => o ?? r.batches.find((b) => b.status === "review")?.id ?? null); })
      .catch((e) => setError(apiErrorMessage(e, "Could not load studio batches.")));
  }, []);
  useEffect(load, [load]);

  if (error) return <p className="mt-bay text-label text-risk">{error}</p>;
  if (!batches) return <Loading label="Loading studio batches…" />;
  return (
    <div className="mt-bay flex flex-col gap-gutter">
      <p className="max-w-[70ch] text-meta text-ink-muted">
        Every colour's photo, cut out and placed in our own studio frame. The product itself is never redrawn or
        recoloured. Nothing here is on the website until you approve the batch, and the supplier's photo is always kept.
      </p>
      {batches.length === 0 && (
        <p className="rounded-lg border border-rule bg-paper px-5 py-4 text-label">
          {notSetUp ? "Studio photos are not set up on this database yet." : "No studio photos yet. The first batch appears here once it is made."}
        </p>
      )}
      {batches.map((b) => (
        <StudioBatchCard key={b.id} batch={b} open={open === b.id} onToggle={() => setOpen(open === b.id ? null : b.id)} onChanged={load} />
      ))}
    </div>
  );
}

function StudioBatchCard({ batch: b, open, onToggle, onChanged }: { batch: StudioBatch; open: boolean; onToggle: () => void; onChanged: () => void }) {
  const [detail, setDetail] = useState<{ sample: StudioImage[]; failed: StudioImage[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<"approve" | "reject" | null>(null);
  const loadDetail = useCallback(() => {
    api<{ sample: StudioImage[]; failed: StudioImage[] }>(`/ingestor/studio/batches/${b.id}`)
      .then(setDetail)
      .catch((e) => setError(apiErrorMessage(e, "Could not load this batch.")));
  }, [b.id]);
  useEffect(() => { if (open && !detail) loadDetail(); }, [open, detail, loadDetail]);

  const decideBatch = async (action: "approve" | "reject") => {
    setBusy(true); setError(null);
    try {
      await api(`/ingestor/studio/batches/${b.id}/decision`, { method: "POST", body: JSON.stringify({ action }) });
      setConfirm(null);
      onChanged();
      loadDetail();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not save that decision."));
    } finally {
      setBusy(false);
    }
  };
  const decideImage = async (img: StudioImage, action: "reject" | "restore") => {
    setError(null);
    try {
      await api(`/ingestor/studio/images/${img.id}/decision`, { method: "POST", body: JSON.stringify({ action }) });
      setDetail((d) => d && { ...d, sample: d.sample.map((s) => (s.id === img.id ? { ...s, status: action === "reject" ? "rejected" : "generated" } : s)) });
      onChanged();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not save that decision."));
    }
  };

  const total = b.generated + b.approved + b.rejected + b.failed;
  const [statusText, statusClass] = BATCH_STATUS[b.status] ?? [b.status, "bg-paper text-ink-muted"];
  return (
    <article className="rounded-lg border border-rule bg-white p-5">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <button type="button" onClick={onToggle} aria-expanded={open} className="text-left text-label font-bold underline-offset-2 hover:underline">
          {b.label}
        </button>
        <span className="text-meta text-ink-muted">
          {ago(b.createdAt)} · {n(total)} photos · {n(b.approved)} live · {n(b.generated)} waiting · {n(b.rejected)} rejected · {n(b.failed)} couldn't be made
        </span>
        <span className={cn("ml-auto rounded px-2 py-0.5 text-meta font-semibold", statusClass)}>{statusText}</span>
      </header>
      {error && <p className="mt-2 text-meta text-risk" role="alert">{error}</p>}
      {open && (
        <div className="mt-4 flex flex-col gap-4">
          {b.generated > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-rule bg-paper px-4 py-3">
              {confirm ? (
                <>
                  <span className="text-label">
                    {confirm === "approve"
                      ? `Put ${n(b.generated)} studio photos on the website? Rejected ones stay off.`
                      : `Reject all ${n(b.generated)} waiting photos? The website keeps the supplier's photos.`}
                  </span>
                  <Button size="sm" variant={confirm === "approve" ? "primary" : "destructive"} disabled={busy} onClick={() => void decideBatch(confirm)}>
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Check className="h-3.5 w-3.5" aria-hidden />}
                    {confirm === "approve" ? "Yes, approve" : "Yes, reject"}
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirm(null)}>Cancel</Button>
                </>
              ) : (
                <>
                  <span className="text-label">Happy with the sample below? Approving puts every waiting photo in this batch on the website.</span>
                  <Button size="sm" className="ml-auto" onClick={() => setConfirm("approve")}><Check className="h-3.5 w-3.5" aria-hidden /> Approve batch</Button>
                  <Button size="sm" variant="outline" onClick={() => setConfirm("reject")}><X className="h-3.5 w-3.5" aria-hidden /> Reject batch</Button>
                </>
              )}
            </div>
          )}
          {!detail ? <Loading inline label="Loading the sample…" /> : (
            <>
              <h3 className="text-label font-bold">A random {n(detail.sample.length)} from this batch</h3>
              <div className="grid gap-4 md:grid-cols-2">
                {detail.sample.map((img) => <StudioPair key={img.id} img={img} onDecide={(a) => void decideImage(img, a)} />)}
              </div>
              <Button size="sm" variant="outline" className="self-start" onClick={() => setDetail(null)}>
                <RefreshCw className="h-3.5 w-3.5" aria-hidden /> Another random sample
              </Button>
              {detail.failed.length > 0 && (
                <section>
                  <h3 className="text-label font-bold">Couldn't be made ({n(detail.failed.length)}): these colours keep the supplier's photo</h3>
                  <ul className="mt-2 divide-y divide-rule rounded-lg border border-rule">
                    {detail.failed.map((f) => (
                      <li key={f.id} className="flex flex-wrap items-baseline gap-x-3 px-4 py-2 text-meta">
                        <span className="font-semibold">{f.listingName}</span>
                        <span>{f.colour}</span>
                        <span className="ml-auto text-ink-muted">{f.reason ?? "Unknown reason"}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      )}
    </article>
  );
}

function StudioPair({ img, onDecide }: { img: StudioImage; onDecide: (a: "reject" | "restore") => void }) {
  const rejected = img.status === "rejected";
  return (
    <figure className={cn("flex flex-col gap-2 rounded-lg border border-rule p-3", rejected && "opacity-60")}>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1">
          <img src={img.sourceUrl} alt={`Supplier photo: ${img.listingName}, ${img.colour}`} loading="lazy" className="aspect-[4/5] w-full rounded bg-paper object-contain" />
          <span className="text-[12px] text-ink-muted">Supplier</span>
        </div>
        <div className="flex flex-col gap-1">
          {img.studioUrl
            ? <img src={img.studioUrl} alt={`Studio photo: ${img.listingName}, ${img.colour}`} loading="lazy" className="aspect-[4/5] w-full rounded bg-paper object-contain" />
            : <div className="aspect-[4/5] w-full rounded bg-paper" />}
          <span className="text-[12px] text-ink-muted">Studio</span>
        </div>
      </div>
      <figcaption className="flex flex-wrap items-center gap-2">
        <span className="text-meta font-semibold">{img.listingName}</span>
        <span className="text-meta text-ink-muted">{img.colour}</span>
        {img.status === "approved" && <span className="rounded bg-ok-bg px-1.5 py-0.5 text-[12px] font-semibold text-ok">Live</span>}
        {rejected
          ? <Button size="sm" variant="ghost" className="ml-auto" onClick={() => onDecide("restore")}><Eye className="h-3.5 w-3.5" aria-hidden /> Undo reject</Button>
          : <Button size="sm" variant="ghost" className="ml-auto" onClick={() => onDecide("reject")}><X className="h-3.5 w-3.5" aria-hidden /> Reject this one</Button>}
      </figcaption>
    </figure>
  );
}
